import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { soccerPath, soccerUrl, runningPath } from '../utils/paths';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { mediaUrl, formatBlogDate } from '../utils/blogUtils';
import { getFeaturedPostsForClub } from '../config/featuredBlogPosts';
import { sortPostsByDate } from '../config/blogPostUtils';
import { canEditPracticeAvailability } from '../utils/practiceSchedule';
import { getPlanBadge } from '../utils/practicePlan';
import { useMyClubMemberships, getMembershipStatusForClub } from './MembershipStatusBanner';
import { normalizeClubSport } from '../config/clubSports';
import './ClubHome.css';
import './PracticePlanSection.css';

const formatPracticeDate = (dateStr) => {
  if (!dateStr) return 'TBA';
  return new Date(dateStr).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMatchDate = formatPracticeDate;

const formatPosition = (p) =>
  p ? p.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : 'N/A';

const renderPracticeCard = (practice, { featured = false } = {}) => {
  const planBadge = getPlanBadge(practice);
  return (
  <div
    key={practice.id}
    className={`club-practice-card${featured ? ' club-practice-card--featured' : ''}`}
  >
    {featured && <p className="club-practice-card__eyebrow">Next up</p>}
    <div className="club-practice-card__info">
      <h3>
        {practice.title}
        {practice.seriesId && (
          <span className="club-practice-card__badge">Recurring</span>
        )}
        <span className={planBadge.className}>{planBadge.label}</span>
      </h3>
      <p className="club-practice-card__date">{formatPracticeDate(practice.practiceDate)}</p>
      {practice.location && (
        <p className="club-practice-card__location">{practice.location}</p>
      )}
    </div>
    <div className="club-practice-card__actions">
      {canEditPracticeAvailability(practice) ? (
        <Link
          to={soccerPath(`/practices/${practice.id}`)}
          className="btn btn-primary"
        >
          Vote Availability
        </Link>
      ) : (
        <Link
          to={soccerPath(`/practices/${practice.id}`)}
          className="btn btn-secondary"
        >
          View Practice
        </Link>
      )}
      {practice.teamFormationId && (
        <Link
          to={soccerPath(`/practices/${practice.id}/lineup`)}
          className="btn btn-secondary"
        >
          View lineup
        </Link>
      )}
    </div>
  </div>
  );
};

const renderMatchCard = (match, { featured = false } = {}) => (
  <div
    key={match.id}
    className={`club-practice-card${featured ? ' club-practice-card--featured' : ''}`}
  >
    {featured && <p className="club-practice-card__eyebrow">Next match</p>}
    <div className="club-practice-card__info">
      <h3>{match.title}</h3>
      {match.opponentName && (
        <p className="club-practice-card__location">vs {match.opponentName}</p>
      )}
      <p className="club-practice-card__date">{formatMatchDate(match.matchDate)}</p>
      {match.location && (
        <p className="club-practice-card__location">{match.location}</p>
      )}
      {match.previewNotes && (
        <p className="club-practice-card__location" style={{ fontStyle: 'italic' }}>
          {match.previewNotes.length > 120
            ? `${match.previewNotes.slice(0, 120)}...`
            : match.previewNotes}
        </p>
      )}
    </div>
    <div className="club-practice-card__actions">
      {match.previewShareToken && (
        <Link
          to={soccerPath(`/public/match-preview/${match.previewShareToken}`)}
          className="btn btn-primary"
        >
          View match preview
        </Link>
      )}
    </div>
  </div>
);

const ClubHome = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isClubCreator, isAuthenticated } = useAuth();
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [myRatings, setMyRatings] = useState({}); // playerId -> 1-5 or null
  const [ratingSubmitting, setRatingSubmitting] = useState(null); // playerId while submitting
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clubPosts, setClubPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [upcomingPractices, setUpcomingPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(false);
  const [publishedMatches, setPublishedMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [publishedTournaments, setPublishedTournaments] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(false);
  const { memberships } = useMyClubMemberships(isAuthenticated ? user?.id : null);

  const loadClub = useCallback(async () => {
    try {
      const data = await api.getClubById(id);
      setClub(data);
      if (normalizeClubSport(data.sport) === 'RUNNING') {
        navigate(runningPath(`/clubs/${id}`), { replace: true });
        return;
      }
    } catch (err) {
      setError(err.message || 'Failed to load club');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadMembers = useCallback(async () => {
    try {
      const data = await api.getClubMembers(id, 'APPROVED', user?.id);
      setMembers(data);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  }, [id, user?.id]);

  const loadClubPosts = useCallback(async () => {
    try {
      setPostsLoading(true);
      const data = await api.getPublishedClubPosts(id);
      setClubPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load club blog posts:', err);
      setClubPosts([]);
    } finally {
      setPostsLoading(false);
    }
  }, [id]);

  const loadUpcomingPractices = useCallback(async () => {
    try {
      setPracticesLoading(true);
      const data = await api.getUpcomingPracticesByClub(id, 5);
      setUpcomingPractices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load upcoming practices:', err);
      setUpcomingPractices([]);
    } finally {
      setPracticesLoading(false);
    }
  }, [id]);

  const loadPublishedMatches = useCallback(async () => {
    try {
      setMatchesLoading(true);
      const data = await api.getPublishedUpcomingMatchesByClub(id);
      setPublishedMatches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load published matches:', err);
      setPublishedMatches([]);
    } finally {
      setMatchesLoading(false);
    }
  }, [id]);

  const loadPublishedTournaments = useCallback(async () => {
    try {
      setTournamentsLoading(true);
      const data = await api.getPublishedClubTournaments(id);
      setPublishedTournaments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load published tournaments:', err);
      setPublishedTournaments([]);
    } finally {
      setTournamentsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadClub();
    loadClubPosts();
    loadPublishedMatches();
    loadPublishedTournaments();
  }, [loadClub, loadClubPosts, loadPublishedMatches, loadPublishedTournaments]);

  const isClubAdmin = () => user && club && club.adminUserId === user.id;

  const clubMembershipStatus = getMembershipStatusForClub(memberships, id);
  const isApprovedClubMember = clubMembershipStatus === 'APPROVED';
  const canAccessClubDetails =
    isApprovedClubMember || isAdmin() || isClubCreator() || isClubAdmin();

  useEffect(() => {
    if (canAccessClubDetails) {
      loadMembers();
    } else {
      setMembers([]);
    }
  }, [canAccessClubDetails, loadMembers]);

  useEffect(() => {
    if (canAccessClubDetails) {
      loadUpcomingPractices();
    } else {
      setUpcomingPractices([]);
    }
  }, [canAccessClubDetails, loadUpcomingPractices]);

  // Load my ratings for each other member (anonymous rating)
  useEffect(() => {
    if (!id || !user?.id || members.length === 0) return;
    const myPlayerId = members.find(m => m.player?.userId === user.id)?.playerId;
    if (!myPlayerId) return;
    const others = members.filter(m => m.playerId !== myPlayerId);
    let cancelled = false;
    const load = async () => {
      const next = {};
      await Promise.all(others.map(async (m) => {
        try {
          const r = await api.getMyRatingForPlayer(id, m.playerId, user.id);
          if (!cancelled) next[m.playerId] = r;
        } catch {
          if (!cancelled) next[m.playerId] = null;
        }
      }));
      if (!cancelled) setMyRatings(next);
    };
    load();
    return () => { cancelled = true; };
  }, [id, user?.id, members]);

  const handleRatePlayer = async (playerId, rating) => {
    if (!user?.id) return;
    setRatingSubmitting(playerId);
    setError('');
    try {
      await api.ratePlayer(id, playerId, user.id, rating);
      setMyRatings(prev => ({ ...prev, [playerId]: rating }));
    } catch (err) {
      setError(err.message || 'Failed to submit rating');
    } finally {
      setRatingSubmitting(null);
    }
  };

  const displayClubPosts = useMemo(() => {
    const featured = getFeaturedPostsForClub(id);
    const featuredSlugs = new Set(featured.map((post) => post.slug));
    const merged = [
      ...featured,
      ...clubPosts.filter((post) => !featuredSlugs.has(post.slug)),
    ];
    return sortPostsByDate(merged);
  }, [id, clubPosts]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  if (!club) {
    return <div className="empty-state">Club not found</div>;
  }

  const nextPractice = upcomingPractices[0] || null;
  const otherUpcomingPractices = upcomingPractices.slice(1);
  const nextMatch = publishedMatches[0] || null;
  const otherPublishedMatches = publishedMatches.slice(1);

  return (
    <div className="club-home">
      <div style={{ marginBottom: '1rem' }}>
        <button
          onClick={() => navigate(soccerPath('/clubs/list'))}
          className="btn btn-secondary"
          style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
        >
          ← Back to Clubs
        </button>
      </div>
      <div className="club-header">
        {club.logoUrl && (
          <img src={mediaUrl(club.logoUrl)} alt={`${club.name} logo`} className="club-logo" />
        )}
        <h1>{club.name}</h1>
        {club.imageUrl && (
          <img src={mediaUrl(club.imageUrl)} alt={club.name} className="club-image" />
        )}
        {club.description && <p className="club-description">{club.description}</p>}
        <div className="club-status">
          <span className={`badge ${club.status === 'APPROVED' ? 'badge-success' : 'badge-warning'}`}>
            {club.status === 'APPROVED' ? 'Approved' : club.status === 'PENDING_APPROVAL' ? 'Pending Approval' : 'Rejected'}
          </span>
        </div>
      </div>

      {clubMembershipStatus === 'PENDING' && (
        <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
          Your membership request for {club.name} is pending club admin approval. Member profiles and
          practice voting will be available once you are approved.
        </div>
      )}

      {isClubAdmin() && club.status === 'APPROVED' && (
        <div className="club-actions">
          <Link to={soccerPath(`/clubs/${id}/admin`)} className="btn btn-primary">
            Club Admin Dashboard
          </Link>
          <Link to={soccerPath(`/clubs/${id}/matches`)} className="btn btn-secondary">
            Schedule Matches
          </Link>
          <Link to={soccerPath(`/clubs/${id}/practices`)} className="btn btn-secondary">
            Schedule Practices
          </Link>
        </div>
      )}

      {(publishedTournaments.length > 0 || tournamentsLoading) && (
        <section className="club-upcoming-practices club-tournaments-section">
          <div className="club-upcoming-practices__header">
            <div>
              <h2>Tournaments</h2>
              <p>Live scores and stats from {club.name} tournaments.</p>
            </div>
            {isClubAdmin() && (
              <Link to={soccerPath(`/clubs/${id}/admin/tournaments`)} className="btn btn-secondary">
                Manage tournaments
              </Link>
            )}
          </div>
          {tournamentsLoading && <p>Loading tournaments…</p>}
          {!tournamentsLoading && publishedTournaments.length > 0 && (
            <div className="club-upcoming-practices__list">
              {publishedTournaments.map((t) => (
                <div key={t.id} className="club-practice-card">
                  <div className="club-practice-card__info">
                    <h3>
                      {t.name}
                      {t.status === 'LIVE' && (
                        <span className="club-practice-card__badge" style={{ background: '#dc3545', color: '#fff' }}>
                          Live
                        </span>
                      )}
                    </h3>
                    {t.description && (
                      <p className="club-practice-card__location">{t.description}</p>
                    )}
                    <p className="club-practice-card__date">
                      {(t.fixtures || []).length} fixture{(t.fixtures || []).length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="club-practice-card__actions">
                    {t.shareToken && (
                      <a
                        href={soccerUrl(`/public/tournament/${t.shareToken}`)}
                        className="btn btn-primary"
                      >
                        View live scores
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="club-upcoming-practices">
        <div className="club-upcoming-practices__header">
          <div>
            <h2>Upcoming Matches</h2>
            <p>Published match previews from {club.name}.</p>
          </div>
          {isClubAdmin() && (
            <Link to={soccerPath(`/clubs/${id}/matches`)} className="btn btn-secondary">
              Schedule Matches
            </Link>
          )}
        </div>
        {matchesLoading && <p>Loading upcoming matches...</p>}
        {!matchesLoading && publishedMatches.length === 0 && (
          <p className="club-upcoming-practices__empty">No published upcoming matches yet.</p>
        )}
        {!matchesLoading && nextMatch && (
          <div className="club-upcoming-practices__featured">
            {renderMatchCard(nextMatch, { featured: true })}
          </div>
        )}
        {!matchesLoading && otherPublishedMatches.length > 0 && (
          <div className="club-upcoming-practices__list">
            <h3 className="club-upcoming-practices__subheading">More upcoming</h3>
            {otherPublishedMatches.map((match) => renderMatchCard(match))}
          </div>
        )}
      </section>

      {canAccessClubDetails && (
        <section className="club-upcoming-practices">
          <div className="club-upcoming-practices__header">
            <div>
              <h2>Upcoming Practices</h2>
              <p>Vote your availability for scheduled club practices.</p>
            </div>
            {isClubAdmin() && (
              <Link to={soccerPath(`/clubs/${id}/practices`)} className="btn btn-secondary">
                Schedule Practices
              </Link>
            )}
          </div>
          {practicesLoading && <p>Loading upcoming practices...</p>}
          {!practicesLoading && upcomingPractices.length === 0 && (
            <p className="club-upcoming-practices__empty">No upcoming practices scheduled.</p>
          )}
          {!practicesLoading && nextPractice && (
            <div className="club-upcoming-practices__featured">
              {renderPracticeCard(nextPractice, { featured: true })}
            </div>
          )}
          {!practicesLoading && otherUpcomingPractices.length > 0 && (
            <div className="club-upcoming-practices__list">
              <h3 className="club-upcoming-practices__subheading">More upcoming</h3>
              {otherUpcomingPractices.map((practice) => renderPracticeCard(practice))}
            </div>
          )}
          <div className="club-upcoming-practices__footer">
            <Link to={soccerPath(`/clubs/${id}/practices/completed`)} className="club-upcoming-practices__link">
              View completed practices →
            </Link>
          </div>
        </section>
      )}

      <section className="club-blog-preview">
        <div className="club-blog-preview__header">
          <div>
            <h2>Latest Club Stories</h2>
            <p>Public news, match recaps, and updates from {club.name}.</p>
          </div>
          <Link to="/blog" className="btn btn-secondary">
            View all blogs
          </Link>
        </div>
        {postsLoading && <p>Loading club stories...</p>}
        {!postsLoading && displayClubPosts.length === 0 && (
          <p className="club-blog-preview__empty">No published club stories yet.</p>
        )}
        {!postsLoading && displayClubPosts.length > 0 && (
          <div className="club-blog-preview__grid">
            {displayClubPosts.slice(0, 3).map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="club-blog-card">
                {post.coverImageUrl && (
                  <img src={mediaUrl(post.coverImageUrl)} alt="" loading="lazy" />
                )}
                <div>
                  <p className="club-blog-card__meta">
                    {post.publishedAt ? formatBlogDate(post.publishedAt) : 'Club story'}
                  </p>
                  <h3>{post.title}</h3>
                  {post.excerpt && <p>{post.excerpt}</p>}
                  <span>Read more →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {canAccessClubDetails ? (
        <div className="club-members">
          <h2>Club Members ({members.length})</h2>
          {members.length === 0 ? (
            <p>No approved members yet.</p>
          ) : (
            <div className="members-list">
              {members.map((member) => {
                const myPlayerId = members.find(m => m.player?.userId === user?.id)?.playerId;
                const isSelf = member.playerId === myPlayerId;
                const myRating = myRatings[member.playerId] ?? null;
                const submitting = ratingSubmitting === member.playerId;
                const photoUrl = member.player?.profilePictureUrl
                  ? mediaUrl(member.player.profilePictureUrl)
                  : null;
                return (
                  <div key={member.id} className="member-card">
                    <Link
                      to={soccerPath(`/clubs/${id}/players/${member.playerId}`)}
                      className="member-card__link"
                    >
                      <div className="member-card__header">
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt={member.player?.name || 'Player'}
                            className="member-card__avatar"
                          />
                        ) : (
                          <div className="member-card__avatar-placeholder">
                            {(member.player?.name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="member-card__info">
                          <h3>{member.player?.name || 'Unknown'}</h3>
                          <p className="member-card__position">
                            {formatPosition(member.player?.position)}
                          </p>
                        </div>
                      </div>
                      {member.roles && member.roles.length > 0 && (
                        <div className="member-roles">
                          {member.roles.map((role, idx) => (
                            <span key={idx} className="badge badge-info">
                              {role.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      )}
                      <span className="member-card__cta">View Profile →</span>
                    </Link>
                    {!isSelf && user && (
                      <div className="member-rate">
                        <span className="member-rate__label">Rate anonymously:</span>
                        <div className="star-rating" role="group" aria-label="Rate player 1 to 5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              className={`star-btn ${myRating >= star ? 'filled' : ''}`}
                              onClick={() => handleRatePlayer(member.playerId, star)}
                              disabled={submitting}
                              title={`${star} star${star > 1 ? 's' : ''}`}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                        {submitting && <span className="member-rate__saving">Saving...</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="club-members club-members--restricted">
          <h2>Club Members</h2>
          <p>
            Member details are available to approved club members and club admins.
            {clubMembershipStatus === 'PENDING'
              ? ' Your join request is waiting for approval.'
              : ' Request to join this club from the clubs page once your player profile is approved.'}
          </p>
          <Link to={soccerPath('/clubs/list')} className="btn btn-primary">
            Browse clubs
          </Link>
        </div>
      )}
    </div>
  );
};

export default ClubHome;

